import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Chip } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeapMaskEntry } from '../../Heap/heapMasks';
import { HeapMaskChip } from './HeapMaskChip';
import { HeapMaskSettingsPopover } from './HeapMaskSettingsPopover';

interface HeapMaskChipBarProps {
  entries: readonly HeapMaskEntry[];
  onVisibilityChange: (maskId: string, visible: boolean) => void;
  onOpacityChange: (maskId: string, opacity: number) => void;
}

export function HeapMaskChipBar({ entries, onVisibilityChange, onOpacityChange }: HeapMaskChipBarProps): ReactElement {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Box display='flex' alignItems='center' gap={1} flex={1} minWidth={0} justifyContent='flex-end'>
      {/* chips that do not fit scroll out of view; the settings popover always lists all masks */}
      <Box
        display='flex'
        alignItems='center'
        gap={1}
        minWidth={0}
        paddingY={0.5}
        sx={{
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {entries.map((entry) => (
          <HeapMaskChip
            key={entry.id}
            label={t(entry.labelKey)}
            color={entry.color}
            enabled={entry.enabled}
            onClick={() => onVisibilityChange(entry.id, !entry.enabled)}
          />
        ))}
      </Box>

      <Chip
        size='small'
        variant='outlined'
        clickable
        icon={<MoreHorizIcon fontSize='small' />}
        label=''
        aria-label={t('heap.masks.settings')}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ flexShrink: 0, '& .MuiChip-icon': { marginRight: '-4px' }, '& .MuiChip-label': { paddingX: 0.5 } }}
      />

      <HeapMaskSettingsPopover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        entries={entries}
        onVisibilityChange={onVisibilityChange}
        onOpacityChange={onOpacityChange}
      />
    </Box>
  );
}
